import DOMPurify from 'dompurify';
import { type PageCalendarEventProps } from './EventTransform';
import { resolveEventLink } from './EventLink';
import { formatListOccurs } from './TimeFormat';

const labelledBlock = (label: string, body: Node | string): HTMLElement => {
    const wrap = document.createElement('div');
    wrap.className = 'pcal-list__block';
    const labelEl = document.createElement('div');
    labelEl.className = 'fc-label';
    labelEl.textContent = label;
    wrap.appendChild(labelEl);
    if (typeof body === 'string') {
        wrap.appendChild(document.createTextNode(body));
    } else {
        wrap.appendChild(body);
    }
    return wrap;
};

const titleLink = (
    props: PageCalendarEventProps,
    title: string,
    eventId: string,
): HTMLElement => {
    const wrap = document.createElement('div');
    wrap.className = 'pcal-list__title';
    const href = resolveEventLink(props, eventId);
    if (href) {
        const anchor = document.createElement('a');
        anchor.href = href;
        anchor.className = 'linkText2';
        anchor.textContent = title;
        wrap.appendChild(anchor);
    } else {
        wrap.textContent = title;
    }
    return wrap;
};

/**
 * Build the custom list-row content for FullCalendar's list views, reproducing
 * the v3 list parity from the available API fields: title link, "Occurs" time,
 * description, and location.
 *
 * The source-calendar block is intentionally omitted from all list views. Meal
 * rows additionally omit the title link and the "Occurs" time, matching v3.
 *
 * Note: v3's map / directions / attachments fields are not returned by the
 * aggregating API and are therefore not rendered.
 */
export const buildListRow = (
    props: PageCalendarEventProps,
    title: string,
    eventId: string,
    isMealCalendar: boolean,
): HTMLElement => {
    const container = document.createElement('div');
    container.className = 'pcal-list__event';

    if (!isMealCalendar && title) {
        container.appendChild(titleLink(props, title, eventId));
    }

    if (!isMealCalendar && !props.allDay) {
        const occurs = formatListOccurs(
            props.startDatetime,
            props.endDatetime,
            props.startTime,
            props.endTime,
            props.siteTimezone,
        );
        if (occurs) {
            container.appendChild(labelledBlock('Occurs', occurs));
        }
    }

    const description = (props.description ?? '').trim();
    if (description) {
        const descEl = document.createElement('div');
        descEl.className = 'pcal-list__block';
        descEl.innerHTML = DOMPurify.sanitize(props.description);
        container.appendChild(descEl);
    }

    const location = (props.address ?? '').trim();
    if (location) {
        container.appendChild(labelledBlock('Location', location));
    }

    return container;
};
